import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';

import {
  DashboardAnomaly,
  DashboardColumn,
  DashboardDevice,
  DashboardInstallationData,
  SortOrder,
  GetColumnInstallationsPayload,
  DashboardFilterPayload,
  DashboardEditColumnPayload,
  DashboardColumnDataWithoutInstallationDetails,
  DashboardFilterName,
  DashboardFilterValues,
} from '@vi-iot/client';

import { GenericStoreStatus } from '../installation-details';
import { Platform } from '../installation-details/installation-details.model';

import { KanbanBoardActions } from './kanban-board.action';
import { KanbanBoardSelectors } from './kanban-board.selectors';
import {
  DashboardFilter,
  DashboardListFilterButtonData,
  MoveKanbanColumnInput,
  QuickViewErrorDetails,
  QuickViewSmartNotes,
} from './kanban-board.model';
import {
  ColumnCreationFlow,
  DashboardInstallationColumn,
  InstallationListDashboardState,
  KanbanColumnPrimaryData,
} from './kanban-board.state';

@Injectable({ providedIn: 'root' })
export class KanbanBoardStore {
  forceQuickViewClose$: Subject<void> = new Subject();

  selectColumnDialogVisibility$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectColumnDialogVisibility
  );
  columns$: Observable<DashboardInstallationColumn[]> = this.store.select(
    KanbanBoardSelectors.selectColumns
  );
  columnDialogData$: Observable<DashboardInstallationColumn> = this.store.select(
    KanbanBoardSelectors.selectColumnDialogData
  );
  lastColumnId$: Observable<number> = this.store.select(KanbanBoardSelectors.selectLastColumnId);
  isTempColumn$: Observable<boolean> = this.store.select(KanbanBoardSelectors.selectIsTempColumn);
  fetchingDashboardStatus$: Observable<GenericStoreStatus> = this.store.select(
    KanbanBoardSelectors.selectFetchingDashboardStatus
  );
  filters$: Observable<DashboardFilter[]> = this.store.select(KanbanBoardSelectors.selectFilters);
  predefinedFilters$: Observable<DashboardFilter[]> = this.store.select(
    KanbanBoardSelectors.selectPredefinedFilters
  );
  selectedFilters$: Observable<DashboardFilter[]> = this.store.select(
    KanbanBoardSelectors.selectSelectedFilters
  );
  selectedAndMappedFilters$: Observable<DashboardFilterPayload> = this.store.select(
    KanbanBoardSelectors.selectSelectedAndMappedFilters
  );
  indexOfQuickViewSelectedDevice$: Observable<number> = this.store.select(
    KanbanBoardSelectors.selectIndexOfSelectedDeviceInQuickView
  );
  quickViewSelectedDevice$: Observable<DashboardDevice> = this.store.select(
    KanbanBoardSelectors.selectQuickViewCurrentDevice
  );
  quickViewErrorsForSpecificDevice$: Observable<QuickViewErrorDetails[]> = this.store.select(
    KanbanBoardSelectors.selectErrorsForSpecificDevice
  );
  quickViewHints$: Observable<QuickViewSmartNotes[]> = this.store.select(
    KanbanBoardSelectors.selectValidHintSmartNotes
  );
  isSmartNotesFetched$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsSmartNotesFetched
  );
  quickViewAnomalies$: Observable<DashboardAnomaly[]> = this.store.select(
    KanbanBoardSelectors.selectQuickViewAnomalies
  );
  quickViewInstallation$: Observable<DashboardInstallationData> = this.store.select(
    KanbanBoardSelectors.selectQuickViewInstallation
  );
  isSelectedDeviceWithSystemLocked$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsSelectedDeviceWithSystemLocked
  );

  isSelectedDeviceWithFLoorHeatingOverHeating$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsSelectedDeviceWithFLoorHeatingOverheating
  );

  isSystemWorkingCorrectly$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsSystemWorkingCorrectly
  );
  allColumnNames$: Observable<string[]> = this.store.select(
    KanbanBoardSelectors.selectAllColumnNames
  );
  forcedStoreUpdateStatus$: Observable<GenericStoreStatus> = this.store.select(
    KanbanBoardSelectors.selectForcedStoreUpdateStatus
  );
  isColumnNameInputVisible$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsColumnNameInputVisible
  );
  isAnySystemStatusData$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsAnySystemStatusData
  );
  columnCreationFlow$: Observable<ColumnCreationFlow> = this.store.select(
    KanbanBoardSelectors.selectColumnCreationFlow
  );
  installationOfTemporaryColumn$: Observable<DashboardInstallationData> = this.store.select(
    KanbanBoardSelectors.selectInstallationOfTemporaryColumn
  );
  idOfOriginalColumnDuringColumnCreationAndMoveProcess$: Observable<number> = this.store.select(
    KanbanBoardSelectors.selectIdOfOriginalColumnDuringColumnCreationAndMoveProcess
  );
  installationListState$: Observable<InstallationListDashboardState> = this.store.select(
    KanbanBoardSelectors.selectInstallationListState
  );
  buttonFilterData$: Observable<DashboardListFilterButtonData[] | null> = this.store.select(
    KanbanBoardSelectors.selectButtonFilterData
  );
  numberOfAllFetchableInstallations$: Observable<number | undefined> = this.store.select(
    KanbanBoardSelectors.selectNumberOfAllFetchableInstallations
  );
  selectIsLimitOfSelfDefinedColumnsHit$: Observable<boolean> = this.store.select(
    KanbanBoardSelectors.selectIsLimitOfSelfDefinedColumnsHit
  );
  installationListCursor$: Observable<string> = this.store.select(
    KanbanBoardSelectors.selectInstallationListCursor
  );

  constructor(private store: Store) {}

  getColumnVersion(columnId: number): Observable<string> {
    return this.store.select(KanbanBoardSelectors.selectColumnVersion(columnId));
  }

  columnsAvailableForInstallation(columnId: number): Observable<KanbanColumnPrimaryData[]> {
    return this.store.select(KanbanBoardSelectors.selectColumnsAvailableForInstallation(columnId));
  }

  getEditColumnPayloadById(id: number): Observable<DashboardEditColumnPayload> {
    return this.store.select(KanbanBoardSelectors.selectEditColumnPayloadById(id));
  }

  isSpecificColumnSelfDefined(id: number): Observable<boolean> {
    return this.store.select(KanbanBoardSelectors.selectIsSpecificColumnSelfDefined(id));
  }

  getPayloadDataBeforeMovingInstallation(
    fistColumnId: number,
    secondColumnId: number
  ): Observable<
    [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails]
  > {
    return this.store.select(
      KanbanBoardSelectors.selectPayloadDataBeforeMovingInstallation(fistColumnId, secondColumnId)
    );
  }

  isFilterButtonActive(
    filterName: DashboardFilterName,
    filterValue: DashboardFilterValues[] | boolean
  ): Observable<boolean> {
    return this.store.select(KanbanBoardSelectors.isFilterButtonActive(filterName, filterValue));
  }

  fetchDashboard(): void {
    this.store.dispatch(KanbanBoardActions.getDashboard.attempt());
  }

  addTempColumn(): void {
    this.store.dispatch(KanbanBoardActions.addTempColumn());
  }

  deleteTempColumn(): void {
    this.store.dispatch(KanbanBoardActions.deleteTempColumn());
  }

  createColumn(name: string, sortOrder: SortOrder): void {
    this.store.dispatch(KanbanBoardActions.addColumn.attempt({ name, sortOrder }));
  }

  deleteColumn(id: number, version: string): void {
    this.store.dispatch(KanbanBoardActions.deleteColumn.attempt({ id, version }));
  }

  editColumnName(columnId: number, data: DashboardEditColumnPayload): void {
    this.store.dispatch(KanbanBoardActions.editColumnName.attempt({ columnId, data }));
  }

  closeColumnDialog(): void {
    this.store.dispatch(KanbanBoardActions.columnDialogActions.close());
  }

  openColumnDialog<T>(modal: ComponentType<T>, column?: DashboardInstallationColumn): void {
    this.store.dispatch(KanbanBoardActions.columnDialogActions.open({ modal, column }));
  }

  setColumnSortOrder(column: DashboardColumn, sortOrder: SortOrder): void {
    this.store.dispatch(KanbanBoardActions.setColumnSortOrder.attempt({ column, sortOrder }));
  }

  fetchFilters(): void {
    this.store.dispatch(KanbanBoardActions.getFilters.attempt());
  }

  applyDashboardFilter(): void {
    this.store.dispatch(KanbanBoardActions.setDashboardFilter.attempt());
  }

  predefineDashboardFilters(filters: DashboardFilter[]): void {
    this.store.dispatch(KanbanBoardActions.predefineDashboardFilter.set({ filters }));
  }

  clearPredefinedDashboardFilters(): void {
    this.store.dispatch(KanbanBoardActions.predefineDashboardFilter.clear());
  }

  openQuickViewPanel(installation: DashboardInstallationData, deviceIndex: number): void {
    this.store.dispatch(KanbanBoardActions.openQuickViewPanel({ installation, deviceIndex }));
  }

  changeSelectedDeviceIndex(deviceIndex: number): void {
    this.store.dispatch(KanbanBoardActions.changeQuickViewDeviceIndex({ deviceIndex }));
  }

  fetchInstallationsForColumn(payload: GetColumnInstallationsPayload): void {
    this.store.dispatch(KanbanBoardActions.fetchInstallationsForColumn.attempt({ payload }));
  }

  copyKanbanInstallation(
    installation: DashboardInstallationData,
    nextColumnId: number,
    previousColumnId: number
  ): void {
    this.store.dispatch(
      KanbanBoardActions.copyKanbanInstallation.attempt({
        installation,
        nextColumnId,
        previousColumnId,
      })
    );
  }

  moveKanbanInstallation(
    installation: DashboardInstallationData,
    nextColumnId: number,
    previousColumnId: number
  ): void {
    this.store.dispatch(
      KanbanBoardActions.moveKanbanInstallation.attempt({
        installation,
        nextColumnId,
        previousColumnId,
      })
    );
  }

  moveKanbanColumn(input: MoveKanbanColumnInput): void {
    this.store.dispatch(
      KanbanBoardActions.moveKanbanColumn.attempt({
        ...input,
      })
    );
  }

  manageColumnNameInput(columnId: number, inputShowed: boolean): void {
    this.store.dispatch(KanbanBoardActions.manageColumnNameInput({ columnId, inputShowed }));
  }

  updateColumnVersions(data: GetColumnInstallationsPayload): void {
    this.store.dispatch(KanbanBoardActions.updateColumnVersions.attempt({ payload: data }));
  }

  updateColumnsAndRefetchInstallations(): void {
    this.store.dispatch(KanbanBoardActions.updateColumnsAndRefetchInstallations.attempt());
  }

  deleteKanbanInstallationCopy(columnId: number, installationId: number): void {
    this.store.dispatch(
      KanbanBoardActions.deleteKanbanInstallationCopy.attempt({ columnId, installationId })
    );
  }

  createInputForNewColumnBeforeMovingInstallation(
    installation: DashboardInstallationData,
    previousColumnId: number
  ): void {
    this.store.dispatch(
      KanbanBoardActions.createNewColumnAndMoveInstallationToIt.createInput({
        installation,
        previousColumnId,
      })
    );
  }

  createNewColumnAndMoveInstallationToIt(newColumnName: string): void {
    this.store.dispatch(
      KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationAttempt({
        newColumnName,
      })
    );
  }

  createInputForNewColumnBeforeCopyingInstallation(installation: DashboardInstallationData): void {
    this.store.dispatch(
      KanbanBoardActions.createNewColumnAndCopyInstallationToIt.createInput({
        installation,
      })
    );
  }

  createNewColumnAndCopyInstallationToIt(newColumnName: string): void {
    this.store.dispatch(
      KanbanBoardActions.createNewColumnAndCopyInstallationToIt.columnCreationAttempt({
        newColumnName,
      })
    );
  }

  initialFetchInstallationList(): void {
    this.store.dispatch(KanbanBoardActions.initialFetchInstallationList.attempt());
  }

  nextFetchInstallationList(limit: number, offset: number): void {
    this.store.dispatch(
      KanbanBoardActions.nextFetchInstallationList.attempt({
        limit,
        offset,
      })
    );
  }

  clearSelectedFilters(): void {
    this.store.dispatch(KanbanBoardActions.clearSelectedFilters());
  }

  checkFilterButtonDataPresenceBeforeFetchingInstallations(): void {
    this.store.dispatch(
      KanbanBoardActions.checkFilterButtonDataPresenceBeforeFetchingInstallations()
    );
  }

  applyFiltersByClickingButton(selectedFilter: DashboardListFilterButtonData): void {
    this.store.dispatch(KanbanBoardActions.applyFiltersByClickingButton({ selectedFilter }));
  }

  navigateToBooking(installation: DashboardInstallationData, platform: Platform): void {
    this.store.dispatch(KanbanBoardActions.navigateToBooking({ installation, platform }));
  }

  refreshSnapshotInInstallationList(): void {
    this.store.dispatch(KanbanBoardActions.refreshSnapshotInInstallationList.attempt());
  }

  closeQuickViewPanel(): void {
    this.store.dispatch(KanbanBoardActions.closeQuickViewPanel());
  }

  recreateSnapshot(): void {
    this.store.dispatch(KanbanBoardActions.recreateSnapshot.attempt());
  }
}
